// ═══════════════════════════════════════════════════════════════════════════
// COTISATIONS PATRONALES SPÉCIALES PAR CODE DmfA — une seule écriture
// ---------------------------------------------------------------------------
// Extrait le 07/10/2026 de app/api/onss/dmfa/route.js (calcul inchangé) pour
// que le COÛT EMPLOYEUR de la fiche lise les mêmes cotisations que la DmfA.
//
// Avant : la DmfA déclarait 6 codes (255, 256, 809, 810, 831, 859 ≈ 0,80 % pour
// une CP 200 de moins de 20 travailleurs) ; le coût employeur de la fiche n'en
// ajoutait qu'un, `cotSpecialeFFE` à 0,17 %, marqué « PÉRIMÉ 2026 » dans
// lois-belges.js. Deux montants pour la même cotisation (R14 bis).
//
// Les taux et leurs sources vivent dans LB.onss.cotisationsDmfa (R1) ; ce
// module n'applique que leurs CONDITIONS : trimestre (256 amiante), CP (831),
// effectif (809 : taux ≥ 20 travailleurs).
// ═══════════════════════════════════════════════════════════════════════════
import { LOIS_BELGES } from './lois-belges';
import { cotisationsParCategorie, CODES_PAR_CATEGORIE, fseCommunsPourCp } from './cotisations-dmfa-categorie';

const r2 = (n) => Math.round(n * 100) / 100;

/**
 * Codes applicables et montants, sur une base donnée (mensuelle ou trimestrielle).
 * `categorie` (catégorie d'employeur ONSS) et `ouvrier` : quand elles sont fournies ET connues de l'ONSS, le FFE (809 ou
 * 811) et les fonds sectoriels (820/825/830/835...) viennent du fichier officiel des taux, par catégorie, et non de la
 * liste fixe. Sans elles (fiche de paie, provisions), le calcul d'avant est INCHANGÉ.
 * `mu` (fraction de prestations du trimestre, arrondie à 0,01) : sans lui, les minimums et forfaits ONSS (825 minimum 80 € · 826 forfait 25 € de
 * la catégorie 083) ne s'appliquent pas (fiche de paie, provisions : inchangé) ; avec lui, 825 = max(taux × base, minimum × µ) et 826 = forfait × µ.
 * @param {{ base:number, cpNum:number, year:number, quarter:number, effectif?:number, codes?:Array, categorie?:string|number, ouvrier?:boolean, finaliteCommerciale?:boolean }} o
 * @returns {{ codes: Array<{code:string, taux:number, amount:number}>, total:number, tauxTotal:number }}
 */
export function cotisationsSpecialesParCode({ base, cpNum, year, quarter, effectif = 0, codes, categorie = null, ouvrier = false, finaliteCommerciale = true, mu = null, cpFonds = null }) {
  const liste = codes || (LOIS_BELGES.onss.cotisationsDmfa || {}).codes || [];
  const parCategorie = categorie != null ? cotisationsParCategorie({ categorie, ouvrier, effectif, finaliteCommerciale }) : null;
  // 253 (vacances annuelles des OUVRIERS, 5,57 %) est dû pour tout ouvrier, quelle que soit la catégorie : catégorie douteuse ou inconnue →
  // on prend le taux de la catégorie générale « 000 » (identique dans les 180 catégories qui le portent). Appelant sans `ouvrier` : inchangé.
  const vacancesRepli = !parCategorie && ouvrier ? cotisationsParCategorie({ categorie: '000', ouvrier: true })?.vacances : null;
  // Catégorie inconnue ou douteuse : les fonds sectoriels sur lesquels TOUTES les catégories de la CP s'accordent (jamais un choix).
  const fseCommuns = !parCategorie && cpFonds ? fseCommunsPourCp({ cp: cpFonds, ouvrier, effectif }) : [];
  const filtrees = liste
    .filter((k) => !k.trimestresDus2026 || year !== 2026 || k.trimestresDus2026.includes(quarter))
    // Catégorie connue : le FFE et les fonds sectoriels viennent de l'ONSS ; on retire ceux de la liste fixe (809, 831...).
    .filter((k) => !parCategorie || !CODES_PAR_CATEGORIE.includes(String(k.code)))
    .filter((k) => !k.cpUniquement || k.cpUniquement.includes(cpNum));
  const lignes = [
    ...filtrees.map((k) => {
      const taux = (effectif >= 20 && k.tauxDes20Travailleurs) ? k.tauxDes20Travailleurs : k.taux;
      return { code: k.code, taux, amount: r2((Number(base) || 0) * taux) };
    }),
    ...(parCategorie ? [parCategorie.ffe, ...parCategorie.fse, parCategorie.vacances].filter(Boolean).map((k) => {
      const surBase = r2((Number(base) || 0) * k.taux);
      if (mu == null) return { code: k.code, taux: k.taux, amount: surBase };
      if (k.forfait > 0) return { code: k.code, taux: 0, amount: r2(k.forfait * mu) };
      if (k.minimum > 0) return { code: k.code, taux: k.taux, amount: Math.max(surBase, r2(k.minimum * mu)) };
      return { code: k.code, taux: k.taux, amount: surBase };
    }) : []),
    ...fseCommuns.filter((k) => !filtrees.some((f) => String(f.code) === String(k.code))).map((k) => ({ code: k.code, taux: k.taux, amount: r2((Number(base) || 0) * k.taux) })),
    ...(vacancesRepli ? [{ code: vacancesRepli.code, taux: vacancesRepli.taux, amount: r2((Number(base) || 0) * vacancesRepli.taux) }] : []),
  ].filter((k) => k.amount > 0);
  const total = r2(lignes.reduce((a, k) => a + k.amount, 0));
  const tauxTotal = Math.round(lignes.reduce((a, k) => a + k.taux, 0) * 1e6) / 1e6;
  return { codes: lignes, total, tauxTotal };
}
